import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Shield, Scale, AlertTriangle, CreditCard } from 'lucide-react';
import PageSeo from '../components/PageSeo';
import SchemaMarkup from '../components/SchemaMarkup';
import { pageSchemas } from '../data/pageSchemas';

const TermsOfService = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      <PageSeo
        title="Terms of Service | CashifyGCMart"
        description="Read the CashifyGCMart terms of service governing use of our gift card exchange platform, submissions, payouts, and buyer/seller responsibilities."
        path="/terms-of-service"
      />
      <SchemaMarkup article={pageSchemas.termsOfService.article} />
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-100 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center mb-4">
            <Scale className="w-12 h-12 mr-4" />
            <h1 className="text-4xl md:text-5xl font-bold">Terms of Service</h1>
          </div>
          <p className="text-xl text-blue-100">
            Professional gift card exchange platform terms and conditions
          </p>
          <div className="mt-4 text-blue-100">
            <span className="text-sm">Last updated: September 24, 2024</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        
        {/* IMPORTANT: Balance Verification Notice - Most Critical Section */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 rounded-2xl shadow-xl p-1 mb-8">
          <div className="bg-white rounded-xl p-6 sm:p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center mr-4">
                <AlertTriangle className="w-7 h-7 text-amber-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900">⚠️ Important Balance Verification Notice</h2>
            </div>
            
            <p className="text-gray-700 leading-relaxed mb-4">
              Many customers do <strong className="text-amber-700">not verify their gift card balance before submission</strong> and later claim that the balance was missing or reduced. We also regularly see customers <strong className="text-amber-700">submitting the same gift card on multiple platforms at the same time</strong> and then contacting us to ask why the balance is no longer available.
            </p>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="font-semibold text-amber-800 mb-3">To be absolutely clear:</p>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5 font-bold">•</span>
                  <span><strong>You MUST verify the gift card balance yourself</strong> before submitting.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-amber-500 mt-0.5 font-bold">•</span>
                  <span><strong>Submit your card to ONE platform only.</strong></span>
                </li>
              </ul>
            </div>

            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 mb-4">
              <p className="text-gray-700 mb-3">
                Once a card is submitted, <strong>we are NOT responsible</strong> for any balance loss, partial balance, or zero balance caused by:
              </p>
              <ul className="space-y-1 text-gray-600 ml-4">
                <li>• Prior use</li>
                <li>• Parallel submissions on other platforms</li>
                <li>• Delays in submission</li>
                <li>• Card sharing or reuse</li>
                <li>• Any activity outside our system</li>
              </ul>
            </div>

            <div className="bg-red-50 border-2 border-red-300 rounded-xl p-4">
              <p className="text-red-800 font-semibold leading-relaxed">
                By submitting a gift card on our platform, <strong>you confirm that you have checked the balance and accept full responsibility</strong> for the card's condition at the time of submission.
              </p>
              <p className="text-red-700 font-bold mt-3 text-lg text-center">
                No exceptions. No disputes. No reversals.
              </p>
            </div>
          </div>
        </div>

        {/* Section 1: Acceptance of Terms */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <FileText className="w-8 h-8 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">1. Acceptance of Terms</h2>
          </div>
          
          <p className="text-gray-700 leading-relaxed mb-4">
            By accessing and using CashifyGCmart ("the Platform", "our Service"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.
          </p>
          
          <p className="text-gray-700 leading-relaxed">
            CashifyGCmart operates as a licensed gift card exchange platform, providing secure marketplace services for the buying and selling of unused gift cards. We maintain the highest standards of security and compliance with all applicable financial regulations.
          </p>
        </div>

        {/* Section 2: Gift Card Validation & Review Disclaimer */}
        <div className="bg-red-50 border-2 border-red-200 rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-red-900">2. Gift Card Validation & Review Disclaimer</h2>
          </div>
          
          <p className="text-red-800 font-semibold leading-relaxed mb-4">
            By submitting your gift card on this platform, you agree to the following:
          </p>
          
          <p className="text-red-800 leading-relaxed mb-4">
            <strong>Once a card is submitted, you must not use, trade, sell, redeem, or attempt to check the balance of the card until our review and validation process is fully completed.</strong>
          </p>
          
          <p className="text-red-800 leading-relaxed mb-4">
            Any changes to the card balance, card activity, or redemption attempts during the review period are fully the customer's responsibility.
          </p>
          
          <div className="mb-4">
            <p className="text-red-800 font-semibold mb-3">We are not responsible for:</p>
            <ul className="list-disc list-inside space-y-2 text-red-800 ml-4">
              <li>Cards used by the customer during validation</li>
              <li>Cards redeemed elsewhere during review</li>
              <li>Balance drops, partial redemptions, or declines caused by prior usage</li>
              <li>Losses caused by sharing the card with anyone else</li>
              <li>Any third-party activity on the card during the review window</li>
            </ul>
          </div>
          
          <p className="text-red-800 font-semibold leading-relaxed mb-4">
            If a card shows reduced balance, is already redeemed, or becomes invalid during our review, the customer bears full responsibility.
          </p>
          
          <p className="text-red-800 leading-relaxed mb-4">
            Submitting a card means you understand that validation may take time, and any activity on the card during this window is strictly "at your own risk."
          </p>
          
          <p className="text-red-800 leading-relaxed mb-4">
            <strong>We do not guarantee payouts for cards that fail, change balance, or show inconsistent activity during the validation process.</strong>
          </p>
          
          <div className="bg-red-100 border border-red-300 rounded-lg p-4 mt-4">
            <p className="text-red-900 font-bold text-center">
              ⚠️ For your own safety: once submitted, do not access or use the card in any way until your payout is completed.
            </p>
          </div>
        </div>

        {/* Section 3: Service Description */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <CreditCard className="w-8 h-8 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">3. Service Description</h2>
          </div>
          
          <div className="space-y-4 text-gray-700">
            <p><strong>Gift Card Exchange:</strong> We provide a secure platform for converting unused gift cards into cash payments.</p>
            <p><strong>Verification Process:</strong> All gift cards undergo professional verification to ensure authenticity and prevent fraud.</p>
            <p><strong>Payment Processing:</strong> We offer same-day payments through multiple secure payment methods including PayPal, Zelle, Cash App, and Bitcoin.</p>
            <p><strong>Rate Transparency:</strong> Our rate calculator provides upfront, transparent pricing with no hidden fees.</p>
          </div>
        </div>

        {/* Section 4: User Requirements */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <Shield className="w-8 h-8 text-purple-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">4. User Requirements & Eligibility</h2>
          </div>
          
          <ul className="space-y-3 text-gray-700">
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Users must be 18 years of age or older</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Must provide accurate personal and payment information</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Gift cards must be legitimately obtained and owned by the user</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Compliance with all applicable local, state, and federal laws</span>
            </li>
            <li className="flex items-start">
              <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
              <span>Submission of clear, unaltered photos of gift cards when required</span>
            </li>
          </ul>
        </div>

        {/* Section 5: Prohibited Activities */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <AlertTriangle className="w-8 h-8 text-red-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">5. Prohibited Activities</h2>
          </div>
          
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6">
            <p className="text-red-800 font-medium mb-2">
              The following activities are strictly prohibited:
            </p>
            <ul className="space-y-2 text-red-700 text-sm">
              <li>• Submission of stolen, fraudulent, or illegally obtained gift cards</li>
              <li>• Providing false or misleading information about card details</li>
              <li>• Attempting to manipulate our verification systems</li>
              <li>• Using the platform for money laundering or other illegal activities</li>
              <li>• Submitting altered or tampered gift card images</li>
              <li>• Creating multiple accounts to circumvent transaction limits</li>
            </ul>
          </div>

          <p className="text-gray-700">
            <strong>Consequences:</strong> Violation of these terms may result in immediate account termination, forfeiture of pending transactions, and reporting to appropriate law enforcement authorities.
          </p>
        </div>

        {/* Section 6: Transaction Process */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <CreditCard className="w-8 h-8 text-indigo-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">6. Transaction Process & Timeline</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Verification Process:</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• Initial submission review: 2-4 hours</li>
                <li>• Card authenticity verification: Same day</li>
                <li>• Balance confirmation: Real-time</li>
                <li>• Final approval: Within 24 hours</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="font-semibold text-gray-900">Payment Timeline:</h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li>• PayPal: Same day</li>
                <li>• Zelle: Same day</li>
                <li>• Cash App: Same day</li>
                <li>• Bitcoin: 2-6 hours</li>
                <li>• Chime: Same day</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 7: Privacy & Security */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <Shield className="w-8 h-8 text-green-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">7. Privacy & Data Security</h2>
          </div>
          
          <div className="space-y-4 text-gray-700">
            <p><strong>Data Protection:</strong> We employ bank-level 256-bit SSL encryption to protect all transmitted data.</p>
            <p><strong>Information Storage:</strong> Personal and gift card information is securely stored and accessed only by authorized personnel.</p>
            <p><strong>Compliance:</strong> Our operations comply with PCI DSS standards and applicable data protection regulations.</p>
            <p><strong>Data Retention:</strong> Transaction data is retained only as long as necessary for business and legal requirements.</p>
          </div>

          <div className="mt-6 p-4 bg-green-50 rounded-lg">
            <p className="text-green-800 text-sm">
              For complete details on our privacy practices, please review our <Link to="/privacy-policy" className="text-green-600 hover:underline font-medium">Privacy Policy</Link>.
            </p>
          </div>
        </div>

        {/* Section 8: Limitations & Disclaimers */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <AlertTriangle className="w-8 h-8 text-orange-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">8. Service Limitations & Disclaimers</h2>
          </div>
          
          <div className="space-y-4 text-gray-700">
            <p><strong>Rate Variations:</strong> Exchange rates may fluctuate based on market conditions and are subject to final verification.</p>
            <p><strong>Service Availability:</strong> While we strive for 99.9% uptime, we cannot guarantee uninterrupted service availability.</p>
            <p><strong>Geographic Limitations:</strong> Our services are currently available to residents of the United States only.</p>
            <p><strong>Transaction Limits:</strong> Daily and monthly transaction limits may apply based on verification level and payment method.</p>
          </div>
        </div>

        {/* Section 9: Contact Information */}
        <div className="bg-white rounded-2xl shadow-lg p-8 mb-8">
          <div className="flex items-center mb-6">
            <FileText className="w-8 h-8 text-blue-600 mr-3" />
            <h2 className="text-2xl font-bold text-gray-900">9. Contact & Support</h2>
          </div>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Customer Support</h3>
              <div className="space-y-2 text-gray-700">
                <p>📧 Email: support@cashifygcmart.com</p>
                <p>💬 Live Chat: Available on website</p>
                <p>🕒 Hours: Monday-Friday, 9 AM - 6 PM PST</p>
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-3">Business Address</h3>
              <div className="text-gray-700">
                <p>2099 Harborview Drive, Suite 210</p>
                <p>San Diego, CA 92101</p>
                <p>United States</p>
              </div>
            </div>
          </div>
        </div>

        {/* SEO content — plain-English summary of key terms */}
        <section className="mt-16 bg-white rounded-2xl shadow-sm p-8 sm:p-10 prose max-w-none">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Terms of Service, explained in plain English</h2>
          <p className="text-gray-700 leading-relaxed mb-4">
            Legal documents can be intimidating. This section summarises the practical parts of our Terms in
            everyday language so you understand exactly what you're agreeing to when you use CashifyGCMart.
            The formal legal text above is still the authoritative version, but nothing here contradicts it.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What you're actually agreeing to</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            When you submit a gift card, you're telling us two things: that you legally own the card (it
            wasn't stolen, found, or bought with someone else's compromised card), and that the balance you
            reported is accurate to the best of your knowledge. In exchange, we agree to pay the quoted
            amount once the card is successfully verified.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">How disputes are handled</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            If the card balance is different from what was reported, we contact you with the correct amount
            and let you choose: accept the revised payout, or cancel the submission at no cost. We never
            quietly adjust payouts without telling you first. If you believe we made an error, reply to the
            confirmation email and a human specialist will review your case within one business day.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Fees and charges</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            We don't charge sellers any fee. The number you see on the rate calculator is exactly what we
            send you — no processing fee, no payout fee, no account fee. Our margin comes from reselling the
            cards, not from fees on sellers.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">What happens if we need to cancel a submission</h3>
          <p className="text-gray-700 leading-relaxed mb-4">
            In rare cases we may cancel a submission — for example, if the retailer freezes a batch of codes,
            or if our fraud engine flags a pattern that needs more review. In those cases no money changes
            hands and you're free to try another service or re-submit after addressing the issue. We always
            explain why.
          </p>

          <h3 className="text-xl font-semibold text-gray-900 mt-6 mb-3">Your rights as a seller</h3>
          <p className="text-gray-700 leading-relaxed">
            You can cancel a submission any time before it's approved. You can request deletion of your
            account data under our Privacy Policy. You're entitled to a written explanation for any rejected
            submission. And you can escalate any dispute to support@cashifygcmart.com for human review.
          </p>
        </section>

        {/* Back to Home */}
        <div className="text-center">
          <Link 
            to="/" 
            className="inline-flex items-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-6 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;